import Button from '@atlaskit/button';
import { colors } from '@atlaskit/theme';
import Spinner from '@atlaskit/spinner';

import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from 'styled-components';

import config from 'appconfig';
import { getEnvironment } from 'appconfig/environment';
import Link from 'components/common/Link';
import Table from 'components/common/Table/Table';
import StopSellingJWMWarningMessage from 'components/navigation/top/StopSellingJWMWarningMessage';
import { Dimensions, Font } from 'components/visuals';
import t from 'modules/i18n/intl';
import { FeatureFlags, checkFeatureGate } from 'util/statsig/index';
import RedirectToProductList from './components/redirectToProductList';
import TableContent, { MACTableType } from './table/TableContent';
import { StyledPipe } from './components/licensesExpandedRow/ActionsSectionElements/StyledPipe';
import { atlassianAccessKeys } from 'data/products/keys';
import EnterpriseSubscriptionsBanner from './components/EnterpriseSubscriptionsbanner';
import MACPageHeader from './components/macPageHeader';
import GreetingMessage from './components/GreetingMessage';
import ServerLicensesMessage from './components/ServerLicensesMessage';
import {
  selectCustomerAccountOverviews,
  selectGetCustomerPartnerId,
  selectOverviewWithOrgChildren,
  selectCustomerAccountOverviewsData,
  selectSwitchedUser,
  selectAsyncCustomerAccountOverviewsByPartnerSearchDTO,
  getMACUserChannel,
} from './duck/selectors';
import { fetchCustomerAccountOverviews } from './duck/actions/accountId';
import {
  CustomerAccountOverviewSearchDTO,
  MACPartnerDataType,
  OverviewEntity,
  OverviewWithOrgChildren,
} from './duck/models';
import { LinkButton } from './components/styledComponents';
import { fetchGetCustomerByEmail } from './duck/actions/contact';
import { State } from '../../model/State';
import SearchLicensesLine from './components/SearchLicensesLine';
import { messages } from './messages';
import Pagination from '../../components/common/Pagination';
import {
  fetchDownloadCsvForEndUserLicenses,
  fetchGetAsyncCustomerAccountOverviews,
} from './duck/actions/accounts';
import { selectPartnerWhitelistStatus } from './duck/selectors/partnerMigration';
import { fetchPartnerWhitelistStatus } from './duck/actions/partnerMigration';
import {
  ENABLE_PRODUCT_OFFERING_KEYMAPPINGS_PARTNER_IDS,
  MIGRATION_ENABLE_CCP_PARTNER_IDS,
} from './constants/constants';
import { fetchProductOfferingKeyMappings } from './duck/actions/product';
import { PRODUCTION } from '../../model/EnvironmentConfig';

const LicensesPageHeader = styled.h2`
  color: ${colors.N800};
  font-size: 24px;
  margin-top: 30px !important;
  font-weight: 500;
  font-family: ${Font.displayFamily};
  letter-spacing: 0.3px;
`;

const StyledLinksWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const PaginationWrapper = styled.div`
  margin-top: 10px;
`;

const LoadingContainer = styled.td`
  padding-top: ${Dimensions.units(7)}px;
  height: 450px;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const DownloadLicensesWrapper = styled.div`
  display: flex;
  margin-top: 10px;
  align-items: baseline;
`;

export enum MACLicensesTableType {
  licenses = 'licenses',
  endUser = 'endUser',
}

const activeTabs = [
  {
    tableType: MACLicensesTableType.endUser,
    label: t('mac.licenses.tab-title.user-end.licenses'),
  },
  {
    tableType: MACLicensesTableType.licenses,
    label: t('mac.licenses.tab-title.licenses'),
  },
];

const tableHeadersDefaultLicenses = [
  {
    display: 'SEN',
    key: 'licenses-sen',
    width: 20,
  },
  {
    display: 'Product',
    key: 'licenses-product',
    width: 30,
  },
  {
    display: 'Name',
    key: 'licenses-site-name',
    width: 15,
  },
  {
    display: 'Support Expires',
    key: 'licenses-support-expires',
    width: 15,
  },
  {
    display: 'Support',
    key: 'licenses-support',
    width: 20,
  },
];

const tableHeadersEndUserLicenses = [
  {
    display: 'SEN',
    key: 'licenses-sen',
    width: 15,
  },
  {
    display: 'Product',
    key: 'licenses-product',
    width: 20,
  },
  {
    display: 'Organization',
    key: 'licenses-organization',
    width: 15,
  },
  {
    display: 'Main Contact',
    key: 'licenses-main-contact',
    width: 15,
  },
  {
    display: 'End Date',
    key: 'licenses-end-date',
    width: 10,
  },
  {
    display: 'Last transaction',
    key: 'licenses-last-transaction',
    width: 20,
  },
];

const Loading = ({ endUserLicensesTabIsChosen }: { endUserLicensesTabIsChosen: boolean }) => (
  <tr>
    <LoadingContainer colSpan={endUserLicensesTabIsChosen ? 6 : 5}>
      <SpinnerWrapper>
        <Spinner size="large" />
      </SpinnerWrapper>
    </LoadingContainer>
  </tr>
);

const isPartnerWhitelistedForMigrationCSVDownload = (partnerId: string | undefined) =>
  partnerId ? MIGRATION_ENABLE_CCP_PARTNER_IDS.includes(Number(partnerId)) : false;

const isWhitelistedForProductOfferingCSVDownload = (partnerId: string | undefined) =>
  partnerId ? ENABLE_PRODUCT_OFFERING_KEYMAPPINGS_PARTNER_IDS.includes(Number(partnerId)) : false;

const overrideAccessAccountIds = (items: OverviewEntity[]): any => {
  return items.map((account) => {
    if (atlassianAccessKeys.includes(account.productKey)) {
      return {
        ...account,
        accountId: account.parentAccountId,
      };
    }
    return account;
  });
};

const removeOrgChildren = (items: OverviewEntity[]): any => {
  return items.filter(
    (acc) => acc.parentAccountId === null || atlassianAccessKeys.includes(acc.productKey),
  );
};

export type LicensesPaginationProps = { maxResult: number; offset: number; totalItems: number };

type Props = {
  senNumber?: string;
  partnerData?: any;
  pagination: LicensesPaginationProps;
  setLicenseExpireDateFilter: Dispatch<SetStateAction<string>>;
  licenseExpireDateFilter?: string;
  onPaginationChange: (
    params: Partial<Omit<LicensesPaginationProps, 'totalItems' | 'maxResult'>>,
  ) => void;
  updateSearchParams: (
    searchObject:
      | CustomerAccountOverviewSearchDTO
      | ((prevVar: CustomerAccountOverviewSearchDTO) => CustomerAccountOverviewSearchDTO),
  ) => void;
  cartEnableCCPSearchCallFGOn?: boolean;
};

const FAQLink = (chunks) => <a href={config.wacPurchaseLicensingUrl}>{chunks}</a>;

const supportLink = (chunks) => <a href={config.support}>{chunks}</a>;

const marketplaceLink = (chunks) => <a href={config.marketplaceUrl}>{chunks}</a>;

const questionsLink = (chunks) => <a href={config.companyContact}>{chunks}</a>;

export const LicensesPage = (props: Props) => {
  const dispatch = useDispatch();
  const [isCollapsedAll, setIsCollapsedAll] = useState(false);
  const [licensesToDisplay, setLicensesToDisplay] = useState<
    Array<OverviewEntity | OverviewWithOrgChildren>
  >([]);
  const [licensesToFilter, setLicensesToFilter] = useState<Array<OverviewEntity>>([]);
  const [licenseTab, setLicenseTab] = useState(MACLicensesTableType.endUser);
  const [isCsvDownloadMigration, setCsvDownloadMigration] = useState(false);

  const endUserLicensesItems = useSelector(selectCustomerAccountOverviews);
  const defaultLicensesItems = overrideAccessAccountIds(
    useSelector(selectOverviewWithOrgChildren) as OverviewEntity[],
  );
  const isLicensesLoading = useSelector(
    (state) =>
      (state as State).mac.accountId.customerAccountOverviews?.inProgress ||
      (state as State).mac.accountId.overviewWithOrgChildren?.inProgress,
  );
  const pollingUuid = useSelector(selectAsyncCustomerAccountOverviewsByPartnerSearchDTO);
  const isPartnerWhitelistStatusMigration =
    useSelector(selectPartnerWhitelistStatus)?.results?.length > 0;
  const { isPartner } = useSelector((state: State) => getMACUserChannel(state));

  const displayMigrationCsvDownloadLink = props.cartEnableCCPSearchCallFGOn
    ? isPartnerWhitelistStatusMigration
    : isPartnerWhitelistedForMigrationCSVDownload(props.partnerData?.id);

  const displayProductOfferingKeyLink =
    isWhitelistedForProductOfferingCSVDownload(props.partnerData?.id) ||
    (props.partnerData?.partner && getEnvironment() !== PRODUCTION);

  const shouldDisplayEndUserLicensesTab = props.partnerData?.id && props.partnerData?.partner;
  const endUserLicensesTabIsChosen =
    shouldDisplayEndUserLicensesTab && licenseTab === MACLicensesTableType.endUser;

  useEffect(() => {
    setLicensesToFilter(
      endUserLicensesTabIsChosen ? endUserLicensesItems : removeOrgChildren(defaultLicensesItems),
    );
  }, [defaultLicensesItems.length, endUserLicensesItems.length, endUserLicensesTabIsChosen]);

  const preselectedLicenseBySen = licensesToFilter.find(
    (license) =>
      license.accountId?.toString() === props.senNumber ||
      license.licenseId?.toString() === props.senNumber?.replace('L', ''),
  );

  useEffect(() => {
    if (isCollapsedAll) {
      setIsCollapsedAll(false);
    }
  }, [isCollapsedAll]);

  useEffect(() => {
    if (pollingUuid && props?.partnerData?.id) {
      dispatch(
        fetchDownloadCsvForEndUserLicenses(
          pollingUuid,
          isCsvDownloadMigration,
          props.partnerData.id,
        ),
      );
    }
  }, [pollingUuid, props?.partnerData?.id]);

  const handleCollapseAll = () => {
    setIsCollapsedAll(true);
  };

  const handleDownloadLicenseOverviewCSV = (isMigration = false) => {
    if (isMigration) {
      setCsvDownloadMigration(isMigration);
    }

    const searchObject = { partnerId: props.partnerData?.id, migration: isMigration };
    dispatch(fetchGetAsyncCustomerAccountOverviews(searchObject));
  };

  const handleDownloadProductOfferingKey = () => {
    dispatch(fetchProductOfferingKeyMappings());
  };

  const switchLicensesTab = (tabType) => setLicenseTab(tabType);

  const shouldShowPagination =
    endUserLicensesTabIsChosen && props.pagination?.maxResult < props.pagination?.totalItems;

  return (
    <>
      <RedirectToProductList />
      <MACPageHeader />
      <GreetingMessage />
      <ServerLicensesMessage />
      <StopSellingJWMWarningMessage />
      <section className="licenses-support-section">
        <LicensesPageHeader>{t('navigation.header.support')}</LicensesPageHeader>
        <ul>
          <li>
            {t(messages.licensesSeeFAQinformation, {
              link: FAQLink,
            })}
          </li>
          <li>
            {t(messages.licensesVisitSupportInformation, {
              link: supportLink,
            })}
          </li>
          <li>
            {t(messages.licensesInformationMarketplace, {
              link: marketplaceLink,
            })}
          </li>
        </ul>
        <br />
        {t(messages.licensesGotMoreQuestions, {
          link: questionsLink,
        })}
      </section>
      <LicensesPageHeader>{t('mac.licenses.title')}</LicensesPageHeader>
      <EnterpriseSubscriptionsBanner defaultLicensesItems={defaultLicensesItems} />
      <StyledLinksWrapper>
        {shouldDisplayEndUserLicensesTab && (
          <>
            <Button
              appearance="link"
              href="https://atlassianpartners.atlassian.net/support?postauth=true#/"
            >
              {t('mac.partner.support.link')}
            </Button>
            <StyledPipe />
          </>
        )}
        <Button appearance="link" href={config.calculatePricingURL}>
          {t('mac.explore.cloud.pricing')}
        </Button>
        <StyledPipe />
        <Button appearance="link">
          <Link to="/purchase/my/license-evaluation">{t('mac.new.trial.license')}</Link>
        </Button>
      </StyledLinksWrapper>
      {shouldDisplayEndUserLicensesTab && (
        <div>
          {activeTabs.map((tab) => (
            <LinkButton
              key={tab.tableType}
              appearance={tab.tableType === licenseTab ? 'link' : 'subtle-link'}
              onClick={() => switchLicensesTab(tab.tableType)}
            >
              {tab.label}
            </LinkButton>
          ))}
        </div>
      )}
      <SearchLicensesLine
        licenseExpireDateFilter={props.licenseExpireDateFilter}
        setLicenseExpireDateFilter={props.setLicenseExpireDateFilter}
        allLicenses={licensesToFilter}
        updateLicensesToDisplay={setLicensesToDisplay}
        updateSearchParams={props.updateSearchParams}
        isPartner={endUserLicensesTabIsChosen}
        partnerId={props?.partnerData?.id}
        senNumber={props?.senNumber}
        preselectedLicenseBySen={preselectedLicenseBySen}
      />
      {!endUserLicensesTabIsChosen && (
        <StyledLinksWrapper>
          <Button appearance="link" onClick={handleCollapseAll}>
            {t(messages.licensesCollapseAll)}
          </Button>
        </StyledLinksWrapper>
      )}
      <Table
        body={
          !isLicensesLoading ? (
            <TableContent
              items={licensesToDisplay}
              tableType={MACTableType.licenses}
              senNumber={props.senNumber}
              isCollapsedAll={isCollapsedAll}
              endUserView={endUserLicensesTabIsChosen}
              isPartner={isPartner}
            />
          ) : (
            <Loading endUserLicensesTabIsChosen={endUserLicensesTabIsChosen} />
          )
        }
        footers={[]}
        headers={
          endUserLicensesTabIsChosen ? tableHeadersEndUserLicenses : tableHeadersDefaultLicenses
        }
        dataAttr="licenses"
      />
      {shouldShowPagination && (
        <PaginationWrapper data-cy="pagination">
          <Pagination
            limit={props.pagination.maxResult}
            offset={props.pagination.offset}
            totalItems={props.pagination.totalItems}
            onPaginationChange={props.onPaginationChange}
          />
        </PaginationWrapper>
      )}
      {endUserLicensesTabIsChosen ? (
        <DownloadLicensesWrapper>
          <Button appearance="link" onClick={() => handleDownloadLicenseOverviewCSV()}>
            {t('mac.licenses.end-user.download-csv')}
          </Button>
          {getEnvironment() !== PRODUCTION || displayMigrationCsvDownloadLink ? (
            <Button appearance="link" onClick={() => handleDownloadLicenseOverviewCSV(true)}>
              {t('mac.licenses.end-user.download-csv.migration')}
            </Button>
          ) : null}
          {displayProductOfferingKeyLink && (
            <Button appearance="link" onClick={() => handleDownloadProductOfferingKey()}>
              {t('mac.licenses.end-user.product.offering.key.mapping')}
            </Button>
          )}
        </DownloadLicensesWrapper>
      ) : null}
    </>
  );
};

export const MACLicensesPage = (props) => {
  const dispatch = useDispatch();
  const cartEnableCCPSearchCall = checkFeatureGate(
    FeatureFlags.CART_MAC_MIGRATION_ENABLE_CCP_SEARCH_CALL,
  );
  const [pagination, setPagination] = useState<LicensesPaginationProps>({
    maxResult: 15,
    offset: 0,
    totalItems: 15,
  });
  const [searchData, setSearchData] = useState<CustomerAccountOverviewSearchDTO>({ partnerId: 0 });
  const [licenseExpireDateFilter, setLicenseExpireDateFilter] = useState<string>();
  const totalResults = useSelector(
    // @ts-expect-error TODO: Fix selector type
    (state) => selectCustomerAccountOverviewsData(state as State)?.totalCount || 0,
  );

  const userEmail = useSelector(selectSwitchedUser);
  const customerPartnerData = useSelector<State, MACPartnerDataType['organisationDetails']>(
    (state) => selectGetCustomerPartnerId(state, userEmail)?.organisationDetails,
  );

  const customerPartnerId = customerPartnerData?.id;
  const customerIsPartner = customerPartnerData?.partner;

  useEffect(() => {
    if (userEmail) {
      dispatch(fetchGetCustomerByEmail(userEmail));
    }
  }, [userEmail]);

  useEffect(() => {
    if (customerPartnerId && customerIsPartner) {
      if (cartEnableCCPSearchCall) {
        dispatch(fetchPartnerWhitelistStatus(customerPartnerId));
      }

      setSearchData({ partnerId: customerPartnerId });
    }
  }, [customerPartnerId, customerIsPartner, cartEnableCCPSearchCall]);

  useEffect(() => {
    if (totalResults > 0) {
      setPagination({
        ...pagination,
        totalItems: totalResults,
      });
    }
  }, [totalResults]);

  const handlePaginationChange = useCallback(
    (params: { offset?: number }) => {
      setPagination({
        ...pagination,
        offset: params.offset || 0,
      });

      dispatch(
        fetchCustomerAccountOverviews(
          { ...searchData, expireDate: licenseExpireDateFilter },
          pagination.maxResult,
          params.offset || 0,
          true,
        ),
      );
    },
    [licenseExpireDateFilter, searchData, pagination.maxResult],
  );

  useEffect(() => {
    if (searchData.partnerId) {
      handlePaginationChange({ offset: 0 });
    }
  }, [searchData.partnerId, handlePaginationChange]);

  return (
    <LicensesPage
      {...props}
      licenseExpireDateFilter={licenseExpireDateFilter}
      setLicenseExpireDateFilter={setLicenseExpireDateFilter}
      partnerData={customerPartnerData}
      pagination={pagination}
      onPaginationChange={handlePaginationChange}
      updateSearchParams={setSearchData}
      cartEnableCCPSearchCallFGOn={cartEnableCCPSearchCall}
    />
  );
};
